<script lang="ts">
  /**
   * The creation modal — create AND edit modes (STEP_7_SPEC.md D3, D6, D8).
   *
   * Mounting contract: the takeover's — a blur-modal sibling of whatever is
   * happening (a page view, a hub, even a focus takeover), with that surface
   * `inert` behind it. The heading takes focus on mount, Esc cancels, and the
   * commit is `aria-disabled` (never `disabled` — the footer's precedent:
   * a disabled control is skipped by screen readers entirely) while the draft
   * is invalid.
   *
   * Authors nothing itself: `onCommit` hands the draft to the container, which
   * runs the §D6 authoring (create) or the §D3 rename (edit).
   *
   * Tag validation mirrors the engine's (A19): no whitespace. Create mode
   * additionally requires a non-empty tag — the `@`-address is what makes the
   * notebook routable — and a fresh create derives one from the name until the
   * tag field is touched. `page_capacity` is page geometry and renders read-only
   * in edit mode (D3): every rendering rule depends on it being one number for
   * the notebook's whole life. The first-task field is not a create-mode field
   * at all: it exists only for the `@tag new task` capture that opened the
   * modal, which is what `initialFirstTask !== undefined` distinguishes.
   */
  import Icon from './Icon.svelte';
  import Check from 'lucide/dist/esm/icons/check.mjs';
  import X from 'lucide/dist/esm/icons/x.mjs';
  import type { Notebook } from '../types/generated';
  import type { NotebookDraft } from '../storage/operations';

  interface Props {
    mode: 'create' | 'edit';
    /** From a capture to an unknown tag (§D4); create mode only. */
    initialTag?: string;
    /**
     * The captured text, becoming the first task (§D4). `undefined` when the
     * modal was not opened by a capture — which is what hides the field.
     */
    initialFirstTask?: string;
    /** Edit mode pre-fill. */
    notebook?: Notebook | null;
    onCommit: (draft: NotebookDraft) => void;
    onCancel: () => void;
  }

  let {
    mode,
    initialTag = '',
    initialFirstTask,
    notebook = null,
    onCommit,
    onCancel,
  }: Props = $props();

  let headingEl = $state<HTMLHeadingElement | null>(null);
  $effect(() => {
    headingEl?.focus();
  });

  // Seeding the fields from props ONCE is the intent: the modal mounts fresh
  // per draft, and a mid-draft prop change would clobber what the user typed.
  // svelte-ignore state_referenced_locally
  let name = $state(mode === 'edit' ? notebook?.name ?? '' : '');
  // svelte-ignore state_referenced_locally
  let tag = $state(mode === 'edit' ? notebook?.tag ?? '' : initialTag);
  // svelte-ignore state_referenced_locally
  let description = $state(mode === 'edit' ? notebook?.description ?? '' : '');
  // svelte-ignore state_referenced_locally
  let pageCapacity = $state(mode === 'edit' ? notebook?.page_capacity ?? 25 : 25);
  // svelte-ignore state_referenced_locally
  let firstTask = $state(initialFirstTask ?? '');

  /**
   * The first-task field belongs to the `@tag new task` capture syntax and to
   * nothing else. Reaching for "New notebook" and being offered a task field
   * implied a first task was wanted; it is not — a notebook's first task is
   * added the way every other one is, by writing it on a page.
   *
   * `undefined` rather than `''` is the whole distinction: a bare `@home-stuff`
   * with no text is still that syntax, so it gets the field, empty.
   */
  const showsFirstTask = $derived(mode === 'create' && initialFirstTask !== undefined);

  /**
   * The tag derives from the name until you take it over.
   *
   * A tag is a routing address (`@workstuff` in a capture), so on a brand-new
   * notebook the name is enough to invent one: lower-kebab it, and the notebook
   * is addressable from the first keystroke. Editing the tag field ends the
   * derivation for good — a deliberate address must survive later edits to the
   * name, and re-deriving would silently rewrite it.
   *
   * Two cases never derive: a tag that arrived from a capture (§D4 — that
   * address is the reason the modal is open at all), and edit mode, where the
   * tag is the real one already.
   */
  // svelte-ignore state_referenced_locally
  let tagIsDerived = $state(mode === 'create' && initialTag === '');

  $effect(() => {
    if (tagIsDerived) tag = slugify(name);
  });

  function onTagInput() {
    tagIsDerived = false;
  }

  /** `Home & Garden!` → `home-garden`. Empty when the name has nothing
   *  sluggable in it, which leaves the commit `aria-disabled` — the same
   *  refusal as an empty tag typed by hand. */
  function slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * The engine's rule (A19): a tag holds no whitespace. The empty tag is the
   * pre-A19 replay default — but a NEW notebook needs an address, so create
   * mode demands non-empty as well. Edit mode with a cleared tag means
   * "unchanged": the container omits it from the rename.
   */
  const tagValid = $derived(!/\s/.test(tag));
  const tagPresent = $derived(mode === 'edit' || tag.trim().length > 0);
  const nameValid = $derived(name.trim().length > 0);
  const valid = $derived(nameValid && tagValid && tagPresent);

  function commit() {
    if (!valid) return;
    onCommit({
      name: name.trim(),
      tag: tag.trim(),
      description,
      pageCapacity,
      firstTask: firstTask.trim() === '' ? undefined : firstTask,
    });
  }

  function onkeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onCancel();
    }
  }
</script>

<!--
  The blur backdrop and the sheet are one element: the blur is decoration,
  `inert` on the SURFACE BEHIND is what controls (the takeover's rule).
-->
<div
  class="modal-backdrop"
  data-testid="notebook-create-modal"
  role="dialog"
  aria-modal="true"
  aria-labelledby="notebook-create-heading"
  tabindex="-1"
  {onkeydown}
>
  <div class="modal-sheet">
    <h1 id="notebook-create-heading" bind:this={headingEl} tabindex="-1">
      {mode === 'create' ? 'New notebook' : 'Edit notebook'}
    </h1>

    <label class="field">
      <span class="field-label">Name</span>
      <input
        type="text"
        data-testid="notebook-create-name"
        bind:value={name}
        autocomplete="off"
        spellcheck="false"
      />
    </label>

    <label class="field">
      <span class="field-label">Tag</span>
      <span class="field-hint">the @-address captures route by — no spaces</span>
      <input
        type="text"
        data-testid="notebook-create-tag"
        bind:value={tag}
        oninput={onTagInput}
        autocomplete="off"
        spellcheck="false"
      />
    </label>

    <label class="field">
      <span class="field-label">Description</span>
      <input
        type="text"
        data-testid="notebook-create-description"
        bind:value={description}
        autocomplete="off"
      />
    </label>

    <label class="field">
      <span class="field-label">Lines per page</span>
      {#if mode === 'edit'}
        <!-- D3: page geometry is immutable — read-only, never disabled. -->
        <input
          type="number"
          data-testid="notebook-create-capacity"
          value={pageCapacity}
          readonly
        />
      {:else}
        <input
          type="number"
          data-testid="notebook-create-capacity"
          bind:value={pageCapacity}
          min="1"
        />
      {/if}
    </label>

    {#if showsFirstTask}
      <label class="field">
        <span class="field-label">First task</span>
        <span class="field-hint">optional — lands on page 1, line 0</span>
        <input
          type="text"
          data-testid="notebook-create-first-task"
          bind:value={firstTask}
          autocomplete="off"
          spellcheck="false"
        />
      </label>
    {/if}

    <div class="modal-actions">
      <button
        class="action"
        type="button"
        data-testid="notebook-create-commit"
        aria-disabled={valid ? 'false' : 'true'}
        onclick={commit}
      ><Icon node={Check} />{mode === 'create' ? '[ Create ]' : '[ Save ]'}</button>
      <button
        class="action"
        type="button"
        data-testid="notebook-create-cancel"
        onclick={onCancel}
      ><Icon node={X} />[ Cancel ]</button>
    </div>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    background: color-mix(in srgb, var(--ink-muted) 20%, transparent);
    backdrop-filter: blur(2px);
    z-index: 30;
  }

  .modal-sheet {
    width: min(100%, 32em);
    background: var(--paper);
    box-shadow: var(--shadow-modal);
    padding: 1.5rem 2rem;
  }

  h1 {
    font-size: 1.25rem;
    font-weight: normal;
    outline: none;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 0.9rem;
  }

  .field-label {
    font-size: 12px;
    color: var(--ink-muted);
  }

  .field-hint {
    font-size: 11px;
    color: var(--ink-muted);
  }

  input {
    font: inherit;
    font-size: 16px;
    border: none;
    border-bottom: 1px solid color-mix(in srgb, var(--ink-muted) 70%, transparent);
    /* The underline IS the whole boundary — there is no box to help it — so it
       needs a stronger mix than a boxed input: 70% muted clears 3:1 in both
       themes (3.12 light / 4.04 dark). Shared by every underlined field. */
    background: transparent;
    padding: 2px 0;
  }

  input[readonly] {
    color: var(--ink-muted);
  }

  .modal-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.4rem;
  }
</style>
